import { useMemo } from 'react';
import { API, endpoints } from 'src/api';
import useSWR, { cache, useSWRInfinite } from 'swr';
export interface UseCreationsProps {
  filter: 'all_creations' | 'in_progress' | 'complete' | 'archived' | 'deleted';
  itemLimit: number;
  mode: 'private' | 'shared' | 'all';
  sortValue: 'recent' | 'last' | 'asc' | 'dsc';
  pageNum: number;
}

export interface ReturnType<DataType> {
  data: DataType;
  error: unknown;
  isLoading: boolean;
  total: number;
  revalidate: () => Promise<boolean>;
}

export const useCreations = ({
  filter,
  itemLimit,
  mode,
  pageNum,
  sortValue,
}: UseCreationsProps): ReturnType<{ [key: string]: unknown }> => {
  endpoints.creations.filter = filter;
  endpoints.creations.itemLimit = itemLimit;
  endpoints.creations.mode = mode;
  endpoints.creations.page = pageNum;
  endpoints.creations.sort = sortValue;

  const { data, error, revalidate } = useSWR(endpoints.creations.url, API.get);
  return {
    data: data?.data?.data?.creations.map((creation) => formatCreationData(creation)) || [],
    total: data?.data?.data?.total || 0,
    error,
    isLoading: !data && !error,
    revalidate,
  };
};

export const useCreationsForLesson = ({ lesson, itemsLimit }): any => {
  const { data, error, size, setSize, revalidate } = useSWRInfinite(
    (index) => {
      const { creationsByLesson } = endpoints;
      creationsByLesson.limit = itemsLimit;
      creationsByLesson.slug = lesson;
      creationsByLesson.start = index;
      return creationsByLesson.url;
    },
    (url) => API.get(url).then((res) => res.data?.data)
  );

  const creationsData = useMemo(
    () =>
      data
        ? [].concat(
            ...data?.map((obj) =>
              obj ? obj?.creations?.map((creation) => formatCreationData(creation)) : []
            )
          )
        : [],
    [data]
  );

  return {
    data: creationsData,
    reminingData: data ? data[data.length - 1]?.remaining || 0 : 0,
    error,
    isLoading: !data && !error,
    setSize,
    size,
    revalidate,
  };
};

export const useCreationsById = ({ id }: { id: string }) => {
  const { creationById } = endpoints;
  creationById.id = id;

  // Bug while navigating creation page so, removing the cache works
  cache.clear();
  const { data, error, revalidate } = useSWR(creationById.url, API.get, {
    revalidateOnFocus: false,
  });

  return {
    data: (data?.data?.data && formatCreationData(data?.data?.data)) || {},
    error,
    isLoading: !data && !error,
    revalidate,
  };
};

export function formatCreationData(creation): any {
  return {
    ...creation,
    title: creation.title,
    tag: creation.tag,
    modified: {
      author: creation.owner.firstname + ' ' + creation.owner.lastname,
      timing: creation.updatedAt,
    },
    tasks: { completed: creation.tasks?.completed, total: creation.tasks?.total },
    image: creation.image,
    lessonSlug: creation.lesson,
    creationId: creation.creationId,
    document: creation.document,
    survey: creation.survey,
    type: creation.type,
    questions: creation.questions,
  };
}

export const useUnlockedCreations = ({ start, limit }: { start: number; limit: number }) => {
  endpoints.unlockedCreations.limit = limit;
  endpoints.unlockedCreations.start = start;

  // Bug while navigating creation page so, removing the cache works
  cache.clear();
  const { data, error, revalidate } = useSWR(endpoints.unlockedCreations.url, API.get);

  return {
    unlockedCreationsdata: data?.data?.data,
    error,
    isLoading: !data && !error,
    revalidate,
  };
};
